/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { compact, map, sortBy, uniq } from 'lodash';
import { branch, compose } from 'recompose';

import logger from 'js/app/loggerSingleton';
import connectToRouter from 'js/lib/connectToRouter';
import Retracked from 'js/lib/retracked';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';
import type { Country } from '@coursera/grpc-types-countrynames/coursera/proto/countrynames/v1/country_names';
import type { MultiGetCountryNamesResponse } from '@coursera/grpc-types-countrynames/coursera/proto/countrynames/v1/country_names_api';

import withNotFound from 'bundles/common/components/withNotFound';
import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';
import ExploreCategoriesSection from 'bundles/partners-graphql/components/cds/ExploreCategoriesSection';
import PartnerDomainsQuery from 'bundles/partners-graphql/components/cds/queries/PartnerDomainsQuery.graphql';
import type { PartnerDomainsQueryQuery } from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerDomainsQuery';
import PartnerBoxes from 'bundles/partners-graphql/components/listing/cds/PartnerBoxes';
import { CountryNamesQuery } from 'bundles/partners-graphql/components/listing/cds/PartnerCountryFilter';
import PartnerFiltersRow from 'bundles/partners-graphql/components/listing/cds/PartnerFiltersRow';
import PartnerListingHero from 'bundles/partners-graphql/components/listing/cds/PartnerListingHero';
import { HIDDEN_PARTNER_COUNTRY_CODES } from 'bundles/partners-graphql/constants';
import CountryNameQuery from 'bundles/partners-graphql/queries/CountryNameQuery';
import type { GetCountryNameResponse } from 'bundles/partners-graphql/queries/CountryNameQuery';
import GetAllPartners from 'bundles/partners-graphql/queries/GetAllPartners.graphql';
import type {
  GetAllDisplayedPartnersQuery,
  GetAllDisplayedPartnersQueryVariables,
  PartnerListingPagePartnerFragment as PartnerListingPagePartner,
} from 'bundles/partners-graphql/queries/__generated__/GetAllPartners';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import _t from 'i18n!nls/partners-graphql';

const PARTNERS_LISTING_PAGE_QUERY_LIMIT = 500;

const styles = {
  partnerListingPage: css`
    /* Empty -- kept as a style anchor for page-level overrides */
  `,
  body: css`
    padding: var(--cds-spacing-400) 0;

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-300) 0;
    }
  `,
};

type PropsFromCaller = {
  countryCode: string;
};

type PropsFromPartnersQuery = {
  partners: Array<PartnerListingPagePartner>;
};

type PropsToComponent = PropsFromCaller & PropsFromPartnersQuery;

export const PartnerListingPage = ({ partners, countryCode }: PropsToComponent) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'' | 'asc' | 'desc'>('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>(countryCode ? [countryCode.toUpperCase()] : []);

  const countryCodes = useMemo(() => {
    return sortBy(
      uniq(compact(map(partners, (partner: PartnerListingPagePartner) => partner?.location?.country?.toUpperCase())))
    ).filter((code) => !HIDDEN_PARTNER_COUNTRY_CODES.includes(code));
  }, [partners]);

  const { data: countryNamesData } = useQuery<{ MultiGetCountryNames?: MultiGetCountryNamesResponse }>(
    CountryNamesQuery,
    {
      variables: {
        countryNamesQueryInput: {
          iso_alpha_2_code: countryCodes,
        },
      },
      skip: countryCodes.length === 0,
    }
  );

  const rawCountries = countryNamesData?.MultiGetCountryNames?.countries;
  const validCountries = rawCountries ? rawCountries.filter((country): country is Country => !!country) : [];
  if (rawCountries && validCountries.length !== rawCountries.length) {
    const count = rawCountries.length - validCountries.length;
    logger.error(`Received null countries. Count${count}`);
  }
  const countries: Country[] = sortBy(validCountries, 'name');

  const countryNamesMap = useMemo(() => {
    const nameMap: Record<string, string> = {};
    countries.forEach((country) => {
      if (country.isoAlpha2Code && country.name) {
        nameMap[country.isoAlpha2Code] = country.name;
      }
    });
    return nameMap;
  }, [countries]);

  const { data: domainsData } = useQuery<PartnerDomainsQueryQuery>(PartnerDomainsQuery, {
    errorPolicy: 'all',
    context: { clientName: 'gatewayGql' },
  });
  const domains = domainsData?.Domain?.getAllDomains ?? [];

  return (
    <div css={styles.partnerListingPage} data-testid="partner-listing-page">
      <PartnerListingHero partners={partners} />
      <div css={styles.body}>
        <PageGridContainer>
          <Grid item xs={12}>
            <PartnerFiltersRow
              countries={countries}
              selectedCountries={selectedCountries}
              onCountriesChange={setSelectedCountries}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortOrder={sortOrder}
              onSortChange={setSortOrder}
            />
            {partners && (
              <PartnerBoxes
                selectedCountries={selectedCountries}
                partners={partners}
                countryNamesMap={countryNamesMap}
                searchQuery={searchQuery}
                sortOrder={sortOrder}
              />
            )}
            <ExploreCategoriesSection domains={domains} />
          </Grid>
        </PageGridContainer>
      </div>
      <CourseraMetatags
        metaNameAndProperties={{
          title: _t('Partners'),
        }}
      />
    </div>
  );
};
PartnerListingPage.LOADING_COMPONENT = LoadingIcon;

export default compose<PropsToComponent, PropsFromCaller>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      page: 'partner_listing',
    },
  })),
  waitForGraphQL<
    PropsFromCaller,
    GetAllDisplayedPartnersQuery,
    GetAllDisplayedPartnersQueryVariables,
    PropsFromPartnersQuery
  >(GetAllPartners, {
    props: ({ data }) => {
      const partners = data?.Partner?.queryAll?.elements.filter(Boolean) || [];
      return { partners };
    },
    options: () => {
      return {
        variables: {
          limit: PARTNERS_LISTING_PAGE_QUERY_LIMIT,
        },
        context: {
          clientName: 'gatewayGql',
        },
      };
    },
  }),
  connectToRouter((params) => {
    return { countryCode: params.location.query.countryCode };
  }),
  withNotFound({
    test: ({ partners, countryCode }) => {
      return (
        !partners ||
        partners?.length === 0 ||
        (!!countryCode && HIDDEN_PARTNER_COUNTRY_CODES.includes(countryCode.toUpperCase()))
      );
    },
  }),
  branch(
    ({ countryCode }: PropsToComponent) => {
      return !!countryCode;
    },
    compose<PropsToComponent, PropsFromCaller & PropsFromPartnersQuery>(
      waitForGraphQL(CountryNameQuery, {
        props: ({ data }: { data?: { GetCountryName?: GetCountryNameResponse } }) => {
          const country = data?.GetCountryName?.country;

          return {
            isInvalidCountryCodePresent: !country,
          };
        },
        options: ({ countryCode }: PropsFromCaller) => {
          return {
            variables: { countryNameQueryInput: { iso_alpha_2_code: countryCode } },
          };
        },
      }),
      withNotFound({
        test: ({ partners, isInvalidCountryCodePresent, countryCode }) => {
          const countryCodesFromPartners = sortBy(
            uniq(
              compact(
                map(
                  partners,
                  (partner: PartnerListingPagePartner) =>
                    partner?.location?.country && partner?.location?.country.toLowerCase()
                )
              )
            )
          );
          const hasNoPartnersForCountryCode = !(
            countryCodesFromPartners && countryCodesFromPartners.includes(countryCode)
          );

          return hasNoPartnersForCountryCode || isInvalidCountryCodePresent;
        },
      })
    )
  )
)(PartnerListingPage);
